import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { withAuthProvider } from '../lib/auth'
import { useMutation } from 'react-apollo'
import { UPDATE_PAYMENT_INTENT } from '../queries/paymentIntent'
import { CREATE_DONATION_INVOICE } from '../queries/invoices'
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js'
import PaymentPanel from '../components/PaymentContainer/PaymentPanel'
import DonationList from '../components/PaymentContainer/DonationList'
import { medicalAndDisaster } from '../helpers/constants'
import { TotalLabel } from '../components/shared.styles'
import { formatCurrency, mapAttributes, validateAmountFormat, isDonationNan, calcAmount } from '../helpers'
import Legal from '../components/PaymentContainer/Legal'
import PaymentForm from '../components/PaymentContainer/PaymentForm'
import qs from 'query-string'

const Donations = ({ me, location, history, paymentIntentId, onSuccess }) => {
  const stripe = useStripe()
  const elements = useElements()
  const { defaultContribution = 0.00 } = qs.parse(location.search)
  const initialDonations = mapAttributes(medicalAndDisaster, { value: validateAmountFormat(defaultContribution) })
  const initialAmount = initialDonations.reduce(calcAmount, 0.0)

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    onBehalf: '',
    memberNumber: null,
    formSubmitted: false,
    stripeError: {},
    donations: initialDonations,
    amount: initialAmount
  })

  const [createDonationInvoice] = useMutation(CREATE_DONATION_INVOICE)
  const [updatePaymentIntent] = useMutation(UPDATE_PAYMENT_INTENT)

  const handleDonationChange = (fund) => ({ target: { value } }) => {
    if (typeof value === 'string' && value === '') value = 0

    if (isDonationNan(value)) return

    setFormData(state => {
      const donations = state.donations.map(item => item.fund === fund
        ? { ...item, value: validateAmountFormat(value) }
        : item)
      const amount = donations.reduce(calcAmount, 0.0)

      return {
        ...state,
        amount,
        donations
      }
    })
  }

  const handleChange = (id) => ({ target: { value } }) =>
    setFormData(state => ({
      ...state,
      [id]: value
    }))

  useEffect(() => {
    if (me) {
      setFormData(state => ({
        ...state,
        email: me.primaryEmail,
        memberNumber: me.id
      }))
    }
  }, [me])

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return
    }

    setFormData(state => ({ ...state, formSubmitted: true }))
    elements.getElement(PaymentElement)

    try {
      await updatePaymentIntent({
        variables: {
          input: {
            id: paymentIntentId,
            description: 'PGA of America Voluntary Contributions',
            amount: formData.amount,
            metadata: {
              donations: formData.donations.map(({ fund }) => fund).join(','),
              donationsAmounts: formData.donations.map(({ value }) => value).join(','),
              email: formData.email,
              entityId: formData.memberNumber,
              firstName: formData.firstName,
              lastName: formData.lastName,
              address: formData.address,
              onBehalf: formData.onBehalf
            }
          }
        }
      })

      // contributions can be made anonymously. This section is only for logged-in users.
      if (formData.memberNumber) {
        const createDonationInvoiceResponses = await Promise.all(formData.donations.map(({ fund, value }) => value > 0 &&
          createDonationInvoice({ variables: { memberNumber: formData.memberNumber, amount: value, fund } })
        ))

        const invoices = createDonationInvoiceResponses.reduce((acc, { data: { createDonationInvoice: { invoices } } }) =>
          [...acc, ...invoices], []
        )

        await updatePaymentIntent({
          variables: {
            input: {
              id: paymentIntentId,
              amount: formData.amount,
              invoices: invoices.map(({ invoiceNum }) => invoiceNum).join(','),
              amounts: invoices.map(({ balance }) => balance).join(',')
            }
          }
        })
      }
      const response = await stripe.confirmPayment({
        elements,
        redirect: 'if_required'
      })

      if (response.error) {
        return setFormData((state) => ({
          ...state,
          formSubmitted: false,
          stripeError: response.error
        }))
      }

      // A successful credit card payment will have status of 'succeeded'. A successful ACH payment will have status of 'processing'.
      if (response.paymentIntent.status === 'succeeded') {
        history.replace('/success?type=contributions')
      }

      history.replace('/success?type=achContributions')
    } catch (e) {
      setFormData(state => ({ ...state, formSubmitted: false }))
      history.push('/error')
    }
  }

  return (
    <PaymentPanel>
      <DonationList
        donations={formData.donations}
        handleChange={handleDonationChange}
      />
      <TotalLabel>Total: {formatCurrency(formData.amount)}</TotalLabel>
      <PaymentForm
        formData={formData}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
      />
      <Legal
        contributions
      />
    </PaymentPanel>
  )
}

export default withRouter(withAuthProvider(Donations))
